import moment from 'moment';
import currency from 'currency.js';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import _omitDeep from 'omit-deep-lodash';

import {
  SHIPMENT_TYPE,
  FORMACTIONS,
  FIELDS_REQUIRED_TO_RATE
} from '../constants/constants';
import { wasTouched } from './Validation';
import _cloneDeep from 'lodash/cloneDeep';
import _isObject from 'lodash/isObject';

export const formatAddress = ({
  city = '',
  state = '',
  postalCode = '',
  country = ''
}) => {
  return `${city}, ${state} ${postalCode}, ${country}`;
};
export const formatFullAddress = ({
  address1 = '',
  city = '',
  state = '',
  postalCode = '',
  country = ''
}) => {
  return `${address1}, ${city}, ${state} ${postalCode}, ${country}`;
};

export const formatPrice = (value, currencySymbol = '$') => {
  if (!value) {
    return '$0.00';
  }

  return currency(value, {
    symbol: currencySymbol,
    formatWithSymbol: true
  }).format();
};

export const buildDropdown = (
  data = [],
  labelField = 'name',
  valueField = 'enumValue'
) => {
  return data.map(item => {
    return { value: item[valueField], title: item[labelField], data: item };
  });
};

export const calculateTime = stop => {
  // Determine the time to use like this:
  // 1) Use the actual time if valued.  If not...
  // 2) Use the projected time if there.  If not...
  // 3) Use the planned time
  return (
    _get(stop, 'actualTimeLocal', null) ||
    _get(stop, 'projectedToTimeLocal', null) ||
    _get(stop, 'plannedToTimeLocal', null)
  );
};

export const formatTime = value => {
  return value ? moment(value).format('ll') : '--';
};

export const sanitizeShipment = (shipmentType, values, userInfo) => {
  const fields = [
    'id',
    'billToInfo',
    'bolInstructions',
    'glCodeId',
    'proNumber',
    'bolNumber',
    'carrierPickupNumber',
    'totalLinearFeet',
    'rateItemGuid',
    'rateListGuid',
    'customRateItemGuid',
    'requestedEquipment',
    'requestedServices',
    'shipmentDirectionId',
    'shipmentModeId',
    'ignoreWarnings',
    'lcpExceptionReasonId',
    'lcpExceptionReasonNotes'
  ];
  if (values.id) {
    // if we actually have a shipment ID we need to ensure row version gets passed in.
    fields.push('rowVersion');
  }

  const shipment = _pick(values, fields);
  shipment.references = sanitizeReferences(values.references);
  shipment.handlingUnits = [];
  shipment.shipmentDirectionId = shipment.shipmentDirectionId
    ? shipment.shipmentDirectionId
    : null;
  shipment.orgId = userInfo.orgId;
  shipment.totalLinearFeet = shipment.totalLinearFeet
    ? shipment.totalLinearFeet
    : null;
  shipment.billToInfo = sanitizeBillToInfo(values.billToInfo);
  shipment.stops = [sanitizeStop(values.pickup), sanitizeStop(values.delivery)];

  shipment.handlingUnits = values.handlingUnits.map(unit => {
    return sanitizeUnit(unit, userInfo);
  });

  shipment.orgId = userInfo.orgId;
  shipment.divisionId = userInfo.divisionId;
  shipment.businessId = userInfo.businessId;

  //TODO currently, we have only the ability to create only one customRate, it need to be modified for multiple customRates
  const customRates = values.shipmentRates.filter(
    shipmentRate => shipmentRate.isCustomRate
  ) || [{}];
  if (customRates.length || !!values.customShipmentRates) {
    const sanitizedCustomRates = sanitizeCustomRate({
      ...customRates[0],
      ...values.customShipmentRates
    });
    shipment.customShipmentRates = sanitizedCustomRates
      ? [sanitizedCustomRates]
      : null;
  }

  return shipment;
};
export const sanitizeBillToInfo = billToInfo => {
  const fields = [
    'billingTerms',
    'billTo',
    'companyName',
    'billToAddress.address1',
    'billToAddress.address2',
    'billToAddress.city',
    'billToAddress.state',
    'billToAddress.postalCode',
    'billToAddress.country',
    'billToAddress.lat',
    'billToAddress.long',
    'billToContactInfo.firstName',
    'billToContactInfo.lastName',
    'billToContactInfo.email',
    'billToContactInfo.phone'
  ];
  return _pick(billToInfo, fields);
};
export const sanitizeProduct = (product, userInfo) => {
  const freightInfo = buildFieldName('product.freightInfo', [
    'freightClassId',
    'nmfcSubCode',
    'nmfcItemCode'
  ]);
  const hazMat = buildFieldName('product.hazMat', [
    'properShippingName',
    'unNumber',
    'contactFirstName',
    'contactLastName',
    'contactPhone',
    'hazMatClassId',
    'hazMatPackingGroupId'
  ]);
  const fields = [
    'id',
    'product.description',
    'product.name',
    'product.sku',
    'product.packageTypeId',
    ...freightInfo,
    ...hazMat,
    'product.pieceCount',
    'product.valueInfo.value',
    'product.weightInfo.weight',
    'product.weightInfo.weightUnit',
    'quantity'
  ];
  const sanitizedProduct = _pick(product, fields);
  sanitizedProduct.product.orgId = userInfo.orgId;
  sanitizedProduct.product.businessId = userInfo.businessId;
  sanitizedProduct.product.divisionId = userInfo.divisionId;
  sanitizedProduct.product.requiresDensityValidation = false;
  sanitizedProduct.product.valueInfo.value = sanitizedProduct.product.valueInfo
    .value
    ? sanitizedProduct.product.valueInfo.value
    : null;
  if (!product.product.isHazMat) {
    sanitizedProduct.product.hazMat = null;
  }

  return sanitizedProduct;
};
export const sanitizeUnit = (unit, userInfo) => {
  const dimensionInfo = buildFieldName('dimensionInfo', [
    'length',
    'width',
    'height',
    'dimensionUnit'
  ]);
  const sanitizedUnit = _pick(unit, [
    'id',
    'unitCount',
    'actualWeightInfo.weight',
    'actualWeightInfo.weightUnit',
    'packageTypeId',
    'pickupStopNumber',
    'deliveryStopNumber',
    'freightInfo.freightClassId',
    'doNotStack',
    ...dimensionInfo
  ]);
  sanitizedUnit.doNotStack = !unit.stackable;
  sanitizedUnit.shipmentItems = unit.shipmentItems.map(product => {
    const sanitizedProduct = sanitizeProduct(product, userInfo);
    return { ...sanitizedProduct };
  });
  return sanitizedUnit;
};
export const sanitizeStop = stop => {
  const fields = [
    'contactInfo.firstName',
    'contactInfo.lastName',
    'contactInfo.email',
    'contactInfo.phone',
    'deliveryNotes',
    'id',
    'locationAddress',
    'locationName',
    'openFromTimeLocal',
    'openToTimeLocal',
    'pickupNotes',
    'plannedToTimeLocal'
  ];
  return _pick(stop, fields);
};
export const sanitizeCustomRate = customRate => {
  if (!customRate) {
    return null;
  }

  const {
    carrier,
    carrierId,
    carrierName,
    estimated_delivery: estimatedDeliveryDate,
    level: serviceLevelIdInCustom,
    notes,
    transit_time: transitDaysInCustom,
    quoteId,
    rate,
    customRateItemGuid,
    carrierGrandTotal,
    carrierQuoteNumber,
    deliveryDateTimeLocal,
    serviceLevelId,
    transitDays
  } = customRate;

  let actualCarrierName = carrierName;
  if (!actualCarrierName) {
    actualCarrierName = _isObject(carrier) ? carrier.name : carrier;
  }

  return {
    carrierName: actualCarrierName,
    carrierId: _isObject(carrier) ? carrier.id : carrierId,
    estimatedDeliveryDate: estimatedDeliveryDate || deliveryDateTimeLocal,
    serviceLevelId: serviceLevelIdInCustom || serviceLevelId,
    notes,
    quoteId: quoteId || carrierQuoteNumber,
    rate: rate || carrierGrandTotal,
    customRateItemGuid,
    transitDays: transitDaysInCustom || transitDays
  };
};
export const buildFieldName = (prefix, fields) => {
  return fields.map(field => `${prefix}.${field}`);
};

export const searchLocations = (locations, searchValue) => {
  const suggestionResults = locations.filter(location => {
    const regex = new RegExp(searchValue, 'i');
    return regex.test(location.title);
  });
  const suggestions = suggestionResults.map(result => {
    return {
      label: result.title,
      ...result
    };
  });

  return suggestions;
};

export const removeUntouchedFields = (action, shipmentValues, touched) => {
  const shipment = _cloneDeep(shipmentValues);
  const handlingUnits = _cloneDeep(shipment.handlingUnits);
  shipment.handlingUnits = [];

  // Determine which Handling Units were touched or are existing
  const anyHandlingUnitTouched = wasTouched(touched, 'handlingUnits');
  if (
    anyHandlingUnitTouched ||
    shipment.id ||
    handlingUnits.some(h => h.fromQuickRate) ||
    action === FORMACTIONS.RATE
  ) {
    handlingUnits.forEach((_unit, unitIndex) => {
      const products = _cloneDeep(_unit.shipmentItems);
      _unit.shipmentItems = [];

      // Determine which Products were touched or are existing for this Handling Unit
      const anyProductsTouched = wasTouched(
        touched,
        `handlingUnits[${unitIndex}].shipmentItems`
      );
      if (anyProductsTouched || _unit.id) {
        products.forEach((_product, productIndex) => {
          const productWasTouched = wasTouched(
            touched,
            `handlingUnits[${unitIndex}].shipmentItems[${productIndex}]`
          );
          if (productWasTouched || _product.id) {
            _unit.shipmentItems.push(_product);
          }
        });
      }

      // Add the Handling Unit to the Shipment if it was touched or is existing
      const handlingUnitTouched = wasTouched(
        touched,
        `handlingUnits[${unitIndex}]`
      );
      if (
        handlingUnitTouched ||
        _unit.id ||
        action === FORMACTIONS.RATE ||
        _unit.fromQuickRate
      ) {
        shipment.handlingUnits.push(_unit);
      }
    });
  }

  return shipment;
};
export const showEquipment = shipmentModeId => {
  return shipmentModeId === SHIPMENT_TYPE.TRUCK_LOAD ? true : false;
};
export const sanitizeReferences = references => {
  const fields = ['stringId', 'values'];

  return references.map(reference => {
    return _pick(reference, fields);
  });
};

export const rateIsntLCP = (selectedRate, rates) => {
  const selectedTotal = selectedRate.carrierGrandTotal;

  return rates.some(rate => {
    return rate.canBeSelected && rate.carrierGrandTotal < selectedTotal;
  });
};

export default {
  formatAddress,
  buildDropdown
};

export const pickRequiredToRateFields = values => {
  const shipment = _cloneDeep(values);
  const sanitizedShipment = _omitDeep(
    shipment,
    'density',
    'totalValue',
    'volume'
  ); //these are calculated on the fly and dont always match the value and datatype of what is stored in the db
  return _pick(sanitizedShipment, FIELDS_REQUIRED_TO_RATE);
};
